import { IInternalAccount } from "@wingspanhq/bookkeeping/dist/lib/interfaces";
import {
  useModalContext,
  useWSSnackbar,
  WSButton,
  WSCircle,
  WSElement,
  WSElementProps,
  WSFlexBox,
  WSIcon,
  WSImage,
  WSModal,
  WSRadioInputGroup,
  WSTable,
  WSTabs,
  WSText
} from "@wingspanhq/fe-component-library";
import {
  IInstantPayoutResponse,
  IPayoutSettingsResponse,
  PayoutPreferences
} from "@wingspanhq/payments/dist/interfaces";
import { IAccount } from "@wingspanhq/users/dist/lib/interfaces";
import { AxiosError } from "axios";
import React, { useEffect, useState } from "react";
import { Route, useHistory } from "react-router-dom";
import howPaymentsWorkImage from "../../../assets/images/how-payments-work.png";
import { CreditCardLogoCircle } from "../../../components/CreditCardBrandLogo/CreditCardLogoCircle";
import { InstitutionLogoCircle } from "../../../components/InstitutionLogo/InstitutionLogoCircle";
import { OverlaySpinner } from "../../../components/OverlaySpinner";
import { getAccountName } from "../../../components/WSAccountsListV2/utils";
import { useVerificationUpgrade } from "../../../Invoices/components/VerificationUpgrade/VerificationUpgrade";
import { useBankingAccount } from "../../../query/bookkeeping/queries";
import { useWSMutation } from "../../../query/helpers";
import { useUserId } from "../../../query/hooks/helpers";
import { useFeatureFlags } from "../../../query/hooks/useFeatureFlags";
import { QUERY_PAYOUT_SETTINGS } from "../../../query/payments/keys";
import {
  useInstantPayoutQuery,
  usePayoutSettings,
  usePayoutSettingsDebitCards
} from "../../../query/payments/queries";
import {
  useAccounts,
  useActivities,
  useMemberProfile
} from "../../../query/users/queries";
import {
  getIsDomesticMember,
  getPossiblePayoutAccounts
} from "../../../query/users/selectors";
import { WSQueries } from "../../../query/WSQuery";
import { ICheckbookCard, paymentsService } from "../../../services/payments";
import {
  getInstantPayoutCardName,
  stripeCardBrandToCreditCardType
} from "../../../types/creditCard";
import { AccountDetails } from "./AccountDetails";
import { CardDetails } from "./CardDetails";
import { DebitCardDetails } from "./DebitDetails";

const INFO_MODAL = "INFO_MODAL";

type Props = { basePath: string } & WSElementProps;

type PayoutMethod =
  | {
      type: "account";
      data: IAccount;
      percentage?: number;
    }
  | { type: "debit"; data: ICheckbookCard; percentage?: number }
  | { type: "card"; data: IInstantPayoutResponse; percentage?: number }
  | { type: "wallet"; data?: IInternalAccount; percentage?: number };

export const PayoutMethodsList: React.FC<Props> = ({
  basePath,
  ...elementProps
}) => {
  const userId = useUserId();
  const featureFlagQuery = useFeatureFlags();
  const accountsQuery = useAccounts();
  const payoutSettingsQuery = usePayoutSettings(userId);
  const payoutSettingsDebitCardsQuery = usePayoutSettingsDebitCards(userId);
  const instantPayoutQuery = useInstantPayoutQuery();
  const bankingAccountQuery = useBankingAccount();
  const history = useHistory();
  const { openSnackbar } = useWSSnackbar();
  const [currentTabIndex, setCurrentTabIndex] = useState(0);
  const { openModal } = useModalContext();
  const memberQuery = useMemberProfile(userId);
  const activityQuery = useActivities(userId);

  useEffect(() => {
    if (history.location.pathname.includes("instant")) {
      console.log("set current tab");
      setCurrentTabIndex(1);
    }
  }, [history.location.pathname]);

  const [changePayoutPrefereces, changePayoutPreferecesMeta] = useWSMutation<
    IPayoutSettingsResponse,
    AxiosError,
    { payoutPreferences: PayoutPreferences }
  >(
    async ({ payoutPreferences }) => {
      return await paymentsService.payoutSettings.update(userId, {
        payoutPreferences
      });
    },
    {
      dependencies: [QUERY_PAYOUT_SETTINGS],
      onSuccess: response => {
        setCurrentTabIndex(
          response.payoutPreferences === PayoutPreferences.Standard ? 0 : 1
        );

        openSnackbar({
          message:
            response.payoutPreferences === PayoutPreferences.Standard
              ? "Standard payouts enabled."
              : "Instant payouts enabled."
        });
      },
      onError: () => {
        openSnackbar({
          message: "Sorry, something went wrong"
        });
      }
    }
  );

  useEffect(() => {
    if (
      payoutSettingsQuery.data &&
      !history.location.pathname.includes("instant")
    ) {
      setCurrentTabIndex(
        payoutSettingsQuery.data.payoutPreferences ===
          PayoutPreferences.Standard
          ? 0
          : 1
      );
    }
  }, [payoutSettingsQuery.status]);

  const bankingAccount = bankingAccountQuery.data;

  const {
    requestVerification,
    requestVerificationMeta,
    isCardPaymentsRequested
  } = useVerificationUpgrade();
  const featureFlags = useFeatureFlags();

  return (
    <WSElement {...elementProps}>
      <WSModal name={INFO_MODAL} title="How do payments on Wingspan work?">
        <WSImage src={howPaymentsWorkImage} placeholder={<></>} mb="M" />
        <WSText mb="S">
          When you get paid, your net income will be deposited to your selected
          destination. If you want, you can automatically send some of each
          payout to your Wingspan Wallet for business expenses.
        </WSText>
        <WSText mb="S">
          First, you receive a payment from a client. If you've created a split
          payment with a collaborator, those payments to your subcontractors
          will be deducted from the invoice and paid to your contractors. If
          your client paid with a card, the standard 2.9% processing fee will be
          subtracted. The resulting funds are your Gross Income.
        </WSText>
        <WSText mb="S">
          Wingspan then sets aside a percentage of your Gross Income for taxes
          resulting in your Net Income (this step is optional and can be changed
          at any time). Your Net Income is money that you can safely spend on
          business and personal expenses.
        </WSText>
        <WSText>
          Last, Wingspan deposits your Net Income into your payout account. You
          also have the option to divide your Net Income between any payout
          account and Wingspan Wallet to save for business expenses.
        </WSText>
      </WSModal>
      <Route
        path={`${basePath}/accounts/:accountId`}
        component={AccountDetails}
      />
      <Route
        path={`${basePath}/cards/:instantPayoutAccountId`}
        component={CardDetails}
      />
      <Route
        path={`${basePath}/debit-cards/:debitCardId`}
        component={DebitCardDetails}
      />
      <WSFlexBox.CenterY wrap="nowrap" mb="XS">
        <WSText.Heading3 mr="S">Payout methods</WSText.Heading3>
        <WSIcon
          name="info"
          color="grey500"
          onClick={() => {
            openModal(INFO_MODAL);
          }}
        />
      </WSFlexBox.CenterY>
      <WSText color="grey600" mb="M">
        Where we send your funds
      </WSText>

      <WSElement>
        <WSQueries
          queries={{
            memberQuery,
            activityQuery,
            accountsQuery,
            payoutSettingsQuery,
            payoutSettingsDebitCardsQuery,
            featureFlagQuery
          }}
        >
          {({
            memberQuery: { data: member },
            activityQuery: { data: activity },
            accountsQuery: { data: accounts },
            payoutSettingsQuery: {
              data: { payoutDestinations = [], payoutPreferences }
            },
            payoutSettingsDebitCardsQuery: { data: payoutSettingsDebitCards },
            featureFlagQuery: { data: featureFlags }
          }) => {
            const isDomesticMember = getIsDomesticMember(member);
            const payoutAccounts = getPossiblePayoutAccounts(accounts);

            if (activity.flows.paymentsSetup?.complete) {
              if (isDomesticMember) {
                return (
                  <>
                    {(changePayoutPreferecesMeta.isLoading ||
                      payoutSettingsQuery.isFetching) && <OverlaySpinner />}

                    <WSRadioInputGroup
                      mb="L"
                      name="method"
                      options={[
                        {
                          value: PayoutPreferences.Standard,
                          label: "Standard Payouts (free)"
                        },
                        {
                          value: PayoutPreferences.Instant,
                          label: "Instant Payouts (1% fee)"
                        }
                      ]}
                      onChange={(event: any) => {
                        changePayoutPrefereces({
                          payoutPreferences: event.target.value
                        });
                      }}
                      value={payoutPreferences}
                    />

                    <WSTabs
                      activeIndex={currentTabIndex}
                      onTabChange={setCurrentTabIndex}
                      tabs={[
                        {
                          label: "Standard",
                          render: () => {
                            const standardPayoutMethods: PayoutMethod[] = [];
                            const otherStandardPayoutMethods: PayoutMethod[] = [];

                            const standardDestination = payoutDestinations.find(
                              destination =>
                                destination.payoutMethod ===
                                PayoutPreferences.Standard
                            );

                            const payoutAccount = payoutAccounts.find(
                              account =>
                                account.accountId ===
                                standardDestination?.destinationId
                            );

                            const payoutAccountPercentage =
                              standardDestination?.percentage || 0;
                            const walletPercentage =
                              100 - payoutAccountPercentage;

                            if (payoutAccount) {
                              standardPayoutMethods.push({
                                type: "account",
                                data: payoutAccount,
                                percentage: payoutAccountPercentage
                              });
                            }
                            if (walletPercentage > 0) {
                              standardPayoutMethods.push({
                                type: "wallet",
                                data: bankingAccount,
                                percentage: walletPercentage
                              });
                            }

                            payoutAccounts
                              .filter(
                                account =>
                                  account.accountId !==
                                  standardDestination?.destinationId
                              )
                              .forEach(account => {
                                otherStandardPayoutMethods.push({
                                  type: "account",
                                  data: account
                                });
                              });

                            if (walletPercentage === 0) {
                              otherStandardPayoutMethods.push({
                                type: "wallet",
                                data: bankingAccount
                              });
                            }

                            return (
                              <>
                                <PayoutAccountsTable
                                  mb="L"
                                  payoutAccounts={standardPayoutMethods}
                                />

                                {otherStandardPayoutMethods.length > 0 && (
                                  <>
                                    <WSText.Heading3 mb="M">
                                      Other Accounts and Cards
                                    </WSText.Heading3>

                                    <PayoutAccountsTable
                                      mb="L"
                                      payoutAccounts={
                                        otherStandardPayoutMethods
                                      }
                                    />
                                  </>
                                )}

                                <WSButton.Link
                                  name="addStandardPayoutMethod"
                                  icon="circlePlus"
                                  onClick={() => {
                                    history.push(
                                      "/member/settings/payment-methods/standard/add"
                                    );
                                  }}
                                >
                                  Add standard payout method
                                </WSButton.Link>
                              </>
                            );
                          }
                        },
                        {
                          label: "Instant",
                          render: () => {
                            const instantPayoutAccounts: PayoutMethod[] = [];
                            const otherInstantPayoutAccounts: PayoutMethod[] = [];

                            const instantDestination = payoutDestinations.find(
                              destination =>
                                destination.payoutMethod ===
                                PayoutPreferences.Instant
                            );
                            const instantAccountPercentage =
                              instantDestination?.percentage || 0;
                            const instantWalletPercentage =
                              100 - instantAccountPercentage;

                            const instantPayoutDebitAccount =
                              instantPayoutQuery.data;

                            let instantPayoutRtpAccount;

                            payoutAccounts
                              .filter(
                                account =>
                                  account.accountId !==
                                    instantDestination?.destinationId &&
                                  account.canBeUsedFor?.rtp
                              )
                              .forEach(account => {
                                otherInstantPayoutAccounts.push({
                                  type: "account",
                                  data: account
                                });
                              });

                            payoutSettingsDebitCards
                              .filter(
                                card =>
                                  card.cardId !==
                                  instantDestination?.destinationId
                              )
                              .forEach(card => {
                                otherInstantPayoutAccounts.push({
                                  type: "debit",
                                  data: card
                                });
                              });

                            if (instantWalletPercentage === 100) {
                              // only the wallet is active
                              instantPayoutAccounts.push({
                                type: "wallet",
                                data: bankingAccount,
                                percentage: instantWalletPercentage
                              });
                            } else {
                              // add the non-wallet account to the list
                              if (instantPayoutDebitAccount) {
                                // this is old school
                                instantPayoutAccounts.push({
                                  type: "card",
                                  data: instantPayoutDebitAccount,
                                  percentage: instantAccountPercentage
                                });
                              } else {
                                instantPayoutRtpAccount = payoutAccounts.find(
                                  account =>
                                    account.accountId ===
                                    instantDestination?.destinationId
                                );
                                if (instantPayoutRtpAccount) {
                                  instantPayoutAccounts.push({
                                    type: "account",
                                    data: instantPayoutRtpAccount,
                                    percentage: instantAccountPercentage
                                  });
                                } else {
                                  const instantPayoutCheckbookAccount = payoutSettingsDebitCards.find(
                                    card =>
                                      card.cardId ===
                                      instantDestination?.destinationId
                                  );
                                  if (instantPayoutCheckbookAccount) {
                                    instantPayoutAccounts.push({
                                      type: "debit",
                                      data: instantPayoutCheckbookAccount,
                                      percentage: instantAccountPercentage
                                    });
                                  }
                                }
                              }

                              // add wallet to list
                              if (instantWalletPercentage > 0) {
                                instantPayoutAccounts.push({
                                  type: "wallet",
                                  data: bankingAccount,
                                  percentage: instantWalletPercentage
                                });
                              } else {
                                otherInstantPayoutAccounts.push({
                                  type: "wallet",
                                  data: bankingAccount
                                });
                              }
                            }

                            return (
                              <>
                                <PayoutAccountsTable
                                  isRtp
                                  mb={
                                    instantPayoutAccounts.length > 0
                                      ? "L"
                                      : "NONE"
                                  }
                                  payoutAccounts={instantPayoutAccounts}
                                />

                                {otherInstantPayoutAccounts.length > 0 && (
                                  <>
                                    <WSText.Heading3 mb="M">
                                      Other Accounts and Cards
                                    </WSText.Heading3>

                                    <PayoutAccountsTable
                                      isRtp
                                      mb="L"
                                      payoutAccounts={
                                        otherInstantPayoutAccounts
                                      }
                                    />
                                  </>
                                )}

                                {featureFlags.rtpAccounts ? (
                                  <>
                                    <WSButton.Link
                                      name="addInstantPayoutMethod"
                                      icon="circlePlus"
                                      onClick={() => {
                                        history.push(
                                          "/member/settings/payment-methods/instant/add"
                                        );
                                      }}
                                    >
                                      Add instant payout method
                                    </WSButton.Link>
                                  </>
                                ) : (
                                  <>
                                    <WSButton.Link
                                      icon="pencil"
                                      data-testid="updatePayoutMethods"
                                      onClick={() => {
                                        history.push(
                                          "/member/settings/payment-methods/instant/replace"
                                        );
                                      }}
                                    >
                                      Replace instant payout method
                                    </WSButton.Link>
                                  </>
                                )}
                              </>
                            );
                          }
                        }
                      ]}
                    />
                  </>
                );
              } else {
                // International user

                const standardDestination = payoutDestinations.find(
                  destination =>
                    destination.payoutMethod === PayoutPreferences.Standard
                );

                const payoutAccount = payoutAccounts.find(
                  account =>
                    account.accountId === standardDestination?.destinationId
                );

                const otherPayoutAccounts = accounts.filter(
                  account =>
                    account.accountId !== standardDestination?.destinationId
                );

                return (
                  <>
                    {payoutAccount && (
                      <PayoutAccountsTable
                        payoutAccounts={[
                          {
                            type: "account",
                            data: payoutAccount,
                            percentage: 100
                          }
                        ]}
                      />
                    )}

                    {otherPayoutAccounts.length > 0 && (
                      <>
                        <WSText.Heading3 mt="M" mb="S">
                          Other payout accounts
                        </WSText.Heading3>

                        <PayoutAccountsTable
                          payoutAccounts={otherPayoutAccounts.map(account => ({
                            type: "account",
                            data: account
                          }))}
                        />
                      </>
                    )}

                    <WSButton.Link
                      mt="M"
                      icon="pencil"
                      onClick={() => {
                        history.push(
                          "/member/settings/payment-methods/international"
                        );
                      }}
                    >
                      Update payout method
                    </WSButton.Link>
                  </>
                );
              }
            }

            // Payments are not initilized
            return (
              <>
                <WSButton.Link
                  name="setUpPayments"
                  onClick={() => {
                    history.push("/member/invoices/set-up");
                  }}
                >
                  Set up payments
                </WSButton.Link>
              </>
            );
          }}
        </WSQueries>
      </WSElement>
    </WSElement>
  );
};

const PayoutAccountsTable: React.FC<{
  payoutAccounts: PayoutMethod[];
  isRtp?: boolean;
} & WSElementProps> = ({ payoutAccounts, isRtp, ...elementProps }) => {
  const history = useHistory();
  return (
    <WSTable
      onRowClick={({ data }) => {
        if (data.type === "account") {
          history.push(
            `/member/settings/payment-methods/accounts/${data.data.accountId}`
          );
        } else if (data.type === "card") {
          history.push(
            "/member/settings/payment-methods/cards/" +
              data.data.instantPayoutAccountId
          );
        } else if (data.type === "debit") {
          history.push(
            `/member/settings/payment-methods/debit-cards/${data.data.cardId}`
          );
        } else {
          history.push("/member/wallet");
        }
      }}
      tableData={payoutAccounts.map((payoutAccount, index) => ({
        id: String(index),
        data: payoutAccount
      }))}
      columns={[
        {
          config: {
            gridTemplateSizeMax: "3fr"
          },
          renderFunction: ({ data }) =>
            data.type === "account" ? (
              <WSFlexBox.CenterY wrap="nowrap">
                <InstitutionLogoCircle
                  color="grey200"
                  mr="S"
                  institutionId={data.data.institutionId}
                />

                <WSText color="grey700">{getAccountName(data.data)}</WSText>
              </WSFlexBox.CenterY>
            ) : data.type === "wallet" ? (
              <WSFlexBox.CenterY wrap="nowrap">
                <WSCircle.Icon
                  color="grey200"
                  secondaryColor="red500"
                  icon="wallet"
                  mr="S"
                />

                <WSText color="grey700">
                  Wingspan Wallet
                  {data.data ? ` (${data.data.numbers.account.slice(-4)})` : ""}
                </WSText>
              </WSFlexBox.CenterY>
            ) : data.type === "debit" ? (
              <WSFlexBox.CenterY wrap="nowrap">
                <WSCircle.Icon
                  color="grey200"
                  secondaryColor="yellow500"
                  icon="creditCard"
                  mr="S"
                />

                <WSText color="grey700">
                  Debit Card
                  {data.data
                    ? ` (${data.data.brand} - ${data.data.last4})`
                    : ""}
                </WSText>
              </WSFlexBox.CenterY>
            ) : (
              <WSFlexBox.CenterY wrap="nowrap">
                <CreditCardLogoCircle
                  color="grey200"
                  mr="S"
                  type={stripeCardBrandToCreditCardType(
                    data.data.instantPayoutAccount.numbers.brand
                  )}
                />

                <WSText color="grey700">
                  {getInstantPayoutCardName(data.data)}
                </WSText>
              </WSFlexBox.CenterY>
            )
        },
        {
          config: {
            gridTemplateSizeMax: "1fr",
            justify: "end"
          },
          renderFunction: ({ data }) =>
            data.percentage ? (
              <WSText color="green500">{data.percentage}%</WSText>
            ) : null
        }
      ]}
      {...elementProps}
    />
  );
};
